import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Menu,
  MenuList,
  MenuItem as MenuListItem,
  ListItemIcon,
  ListItemText,
  Fab,
  Tooltip,
  Badge,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemAvatar,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Star as StarIcon,
  Visibility as VisibilityIcon,
  GetApp as ExportIcon,
  FilterList as FilterIcon,
  History as HistoryIcon,
  Payment as PaymentIcon,
  Flight as FlightIcon,
  Hotel as HotelIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { SettingsContext } from '../../contexts/SettingsContext';
import { 
  useClientsData, 
  useReservationsData, 
  useFinancialData,
  addClient,
  updateClient,
  deleteClient
} from '../../services/dataService';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const Clients = () => {
  const { settings, formatCurrency, formatDate } = useContext(SettingsContext);
  const navigate = useNavigate();
  
  // Get data from data services
  const { clients: clientsData, loading: clientsLoading, error: clientsError } = useClientsData();
  const { reservations: reservationsData, loading: reservationsLoading, error: reservationsError } = useReservationsData();
  const { transactions: financialData, loading: financialLoading, error: financialError } = useFinancialData();
  
  // Local state
  const [filteredClients, setFilteredClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [detailsTab, setDetailsTab] = useState(0);
  
  // Combined loading state
  const isLoading = clientsLoading || reservationsLoading || financialLoading;
  
  // Form data for adding/editing clients
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    adresse: '',
    ville: '',
    pays: '',
    statut: 'Actif'
  });
  
  // Helper function to ensure consistent ID comparison
  const normalizeId = (id) => {
    return id !== undefined && id !== null ? Number(id) : null;
  };
  
  // Process client data with reservations and financial data
  const processedClients = React.useMemo(() => {
    if (clientsLoading || reservationsLoading || financialLoading) {
      return [];
    }
    
    return clientsData.map(client => {
      // Normalize client ID for consistent comparison
      const normalizedClientId = normalizeId(client.id);
      
      // Count client's reservations - match both numeric and string IDs
      const clientReservations = reservationsData.filter(r => 
        normalizeId(r.clientId) === normalizedClientId
      );
      const nombreReservations = clientReservations.length;
      
      // Calculate total spent - match both numeric and string IDs
      const clientTransactions = financialData.filter(t => 
        normalizeId(t.clientId) === normalizedClientId
      );
      const totalDepense = clientTransactions.reduce((sum, t) => sum + t.montant, 0);
      
      // Determine last visit date (most recent reservation)
      const sortedReservations = [...clientReservations].sort(
        (a, b) => new Date(b.dateReservation) - new Date(a.dateReservation)
      );
      const derniereVisite = sortedReservations.length > 0 
        ? sortedReservations[0].dateReservation 
        : client.dateInscription || '-';
      
      // Add status if not present
      const statut = client.statut || (nombreReservations > 0 ? 'Actif' : 'Inactif');
      
      // Add inscription date if not present
      const dateInscription = client.dateInscription || '2024-01-01'; // Default date
      
      // Handle location data - preserve existing values if present
      let ville = client.ville;
      let pays = client.pays;
      
      // Only try to extract or provide location data if both ville and pays are missing
      if ((!ville || !pays) && client.adresse) {
        const addressParts = client.adresse.split(',');
        if (addressParts.length >= 2) {
          ville = ville || addressParts[0].trim();
          pays = pays || addressParts[addressParts.length - 1].trim();
        } else if (addressParts.length === 1) {
          // If only one part, use it as ville
          ville = ville || addressParts[0].trim();
        }
      }
      
      // Only provide sample data if values are still missing
      if ((!ville || !pays) && client.id) {
        // Map well-known client IDs to locations from sample data
        const locationMap = {
          1: { ville: 'Alger', pays: 'Algérie' },
          2: { ville: 'Oran', pays: 'Algérie' },
          3: { ville: 'Constantine', pays: 'Algérie' },
          4: { ville: 'Annaba', pays: 'Algérie' },
          5: { ville: 'Tlemcen', pays: 'Algérie' }
        };
        
        if (locationMap[client.id]) {
          ville = ville || locationMap[client.id].ville;
          pays = pays || locationMap[client.id].pays;
        }
      }
      
      return {
        ...client,
        nombreReservations,
        totalDepense,
        derniereVisite,
        statut,
        dateInscription,
        niveauFidelite: client.niveauFidelite || 'Bronze',
        preferencesVoyage: client.preferencesVoyage || [],
        ville: ville || '',
        pays: pays || ''
      };
    });
  }, [clientsData, reservationsData, financialData, clientsLoading, reservationsLoading, financialLoading]);

  // Update filtered clients when data changes
  useEffect(() => {
    if (processedClients.length > 0) {
      let filtered = processedClients.filter(client => {
        const matchesSearch = 
          client.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
          client.prenom.toLowerCase().includes(searchTerm.toLowerCase()) ||
          client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (client.telephone && client.telephone.includes(searchTerm));
        
        const matchesFilter = filterStatus === 'all' || client.statut === filterStatus;
        
        return matchesSearch && matchesFilter;
      });
      
      setFilteredClients(filtered);
      setPage(0);
    }
  }, [searchTerm, filterStatus, processedClients]);

  const handleOpenDialog = (client = null) => {
    if (client) {
      setEditingClient(client);
      setFormData({
        nom: client.nom,
        prenom: client.prenom,
        email: client.email,
        telephone: client.telephone,
        adresse: client.adresse || '',
        ville: client.ville || '',
        pays: client.pays || '',
        statut: client.statut
      });
    } else {
      setEditingClient(null);
      setFormData({
        nom: '',
        prenom: '',
        email: '',
        telephone: '',
        adresse: '',
        ville: '',
        pays: '',
        statut: 'Actif'
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingClient(null);
    setFormData({
      nom: '',
      prenom: '',
      email: '',
      telephone: '',
      adresse: '',
      ville: '',
      pays: '',
      statut: 'Actif'
    });
  };

  const handleSaveClient = () => {
    if (editingClient) {
      // Update an existing client with all necessary fields
      const updatedClient = { 
        ...editingClient, 
        nom: formData.nom,
        prenom: formData.prenom,
        email: formData.email,
        telephone: formData.telephone,
        adresse: formData.adresse,
        ville: formData.ville,
        pays: formData.pays,
        statut: formData.statut
      };
      
      // Use the direct update function
      updateClient(updatedClient);
      
      setSnackbar({
        open: true,
        message: 'Client modifié avec succès',
        severity: 'success'
      });
    } else {
      // Create a new client
      const newClient = {
        ...formData,
        id: Date.now().toString(), // Generate unique ID as string
        dateInscription: new Date().toISOString().split('T')[0],
        nombreReservations: 0,
        totalDepense: 0,
        derniereVisite: '-',
        niveauFidelite: 'Bronze',
        preferencesVoyage: []
      };
      
      console.log("Creating new client:", newClient);
      
      // Use the direct add function
      addClient(newClient);
      
      setSnackbar({
        open: true,
        message: 'Client ajouté avec succès',
        severity: 'success'
      });
    }
    handleCloseDialog();
  };

  const handleDeleteClient = (clientId) => {
    // Use the direct delete function
    deleteClient(clientId);
    
    setSnackbar({
      open: true,
      message: 'Client supprimé avec succès',
      severity: 'success'
    });
    setAnchorEl(null);
  };

  const handleMenuClick = (event, client) => {
    setAnchorEl(event.currentTarget);
    setSelectedClient(client);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedClient(null);
  };

  const handleViewDetails = () => {
    setOpenDetailsDialog(true);
    setAnchorEl(null);
  };

  const getInitials = (nom, prenom) => {
    const firstLetter = nom && typeof nom === 'string' ? nom.charAt(0) : '';
    const secondLetter = prenom && typeof prenom === 'string' ? prenom.charAt(0) : '';
    return `${firstLetter}${secondLetter}`.toUpperCase();
  };

  const getClientReservations = (clientId) => {
    if (reservationsLoading || !reservationsData) {
      return [];
    }
    const normalizedClientId = normalizeId(clientId);
    return reservationsData.filter(r => normalizeId(r.clientId) === normalizedClientId);
  };

  const getClientTransactions = (clientId) => {
    if (financialLoading || !financialData) {
      return [];
    }
    const normalizedClientId = normalizeId(clientId);
    return financialData.filter(t => normalizeId(t.clientId) === normalizedClientId);
  };

  const getFidelityColor = (niveau) => {
    switch(niveau) {
      case 'Premium': return 'error';
      case 'Standard': return 'info';
      case 'Bronze': return 'warning';
      default: return 'default';
    }
  };

  // Calculate stats for the dashboard cards
  const statsCards = React.useMemo(() => {
    if (isLoading) {
      return [
        { title: 'Total Clients', value: '-', icon: PeopleIcon, color: 'primary', trend: '-' },
        { title: 'Clients Actifs', value: '-', icon: TrendingUpIcon, color: 'success', trend: '-' },
        { title: 'Nouveaux ce mois', value: '-', icon: PersonIcon, color: 'info', trend: '-' },
        { title: 'Revenus Total', value: '-', icon: PaymentIcon, color: 'warning', trend: '-' }
      ];
    }

    const currentMonth = new Date().getMonth();
    const activeClients = processedClients.filter(c => c.statut === 'Actif').length;
    const newClients = processedClients.filter(c => {
      const inscriptionDate = new Date(c.dateInscription);
      return inscriptionDate.getMonth() === currentMonth;
    }).length;
    const totalRevenue = processedClients.reduce((sum, c) => sum + c.totalDepense, 0);

    return [
      {
        title: 'Total Clients',
        value: processedClients.length,
        icon: PeopleIcon,
        color: 'primary',
        trend: '+12%'
      },
      {
        title: 'Clients Actifs',
        value: activeClients,
        icon: TrendingUpIcon,
        color: 'success',
        trend: `${Math.round((activeClients / processedClients.length) * 100)}%`
      },
      {
        title: 'Nouveaux ce mois',
        value: newClients,
        icon: PersonIcon,
        color: 'info',
        trend: '+8%'
      },
      {
        title: 'Revenus Total',
        value: formatCurrency(totalRevenue),
        icon: PaymentIcon,
        color: 'warning',
        trend: '+15%'
      }
    ];
  }, [processedClients, isLoading, formatCurrency]);

  // Function to format currency correctly
  const formatCurrencyForPDF = (amount) => {
    // Convert number to string with spaces as thousand separators
    const numberPart = Math.round(amount || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    return `${numberPart} DZD`;
  };

  const exportClientsToPDF = () => {
    try {
      // Create a new PDF document
      const doc = new jsPDF('landscape');
      
      // Document dimensions
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      
      // Colors
      const primaryColor = [30, 64, 175]; // blue
      const secondaryColor = [124, 58, 237]; // purple
      const textColor = [31, 41, 55]; // dark gray
      
      // Add header
      doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.rect(0, 0, pageWidth, 25, 'F');
      
      // Replace logo placeholder with agency logo and name
      if (settings.agencyLogo) {
        try {
          // Add the agency logo from base64 string
          doc.addImage(settings.agencyLogo, 'PNG', margin, 5, 20, 15, undefined, 'FAST');
          
          // Add agency name
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text(settings.agencyName || 'Agence de Voyage', margin + 25, 15);
        } catch (logoError) {
          console.error('Error adding logo to PDF:', logoError);
          // Fallback to text only
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
        }
      } else {
        // No logo, just add agency name
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
      }
      
      // Add title
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text("Liste des Clients", pageWidth - margin, 17, { align: 'right' });
      
      // Add date
      doc.setTextColor(textColor[0], textColor[1], textColor[2]);
      doc.setFontSize(10);
      doc.text(`Généré le: ${new Date().toLocaleDateString('fr-FR')}`, margin, 35);
      
      // Add summary section
      doc.setDrawColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
      doc.setLineWidth(0.5);
      doc.line(margin, 40, pageWidth - margin, 40);
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text("Résumé", margin, 50);
      
      const stats = [
        { label: "Nombre total de clients", value: `${filteredClients.length}` },
        { label: "Clients actifs", value: `${filteredClients.filter(c => c.statut === 'Actif').length} (${Math.round(filteredClients.filter(c => c.statut === 'Actif').length / filteredClients.length * 100)}%)` },
        { label: "Clients premium", value: `${filteredClients.filter(c => c.niveauFidelite === 'Premium').length} (${Math.round(filteredClients.filter(c => c.niveauFidelite === 'Premium').length / filteredClients.length * 100)}%)` },
        { label: "Revenu total", value: formatCurrencyForPDF(filteredClients.reduce((sum, client) => sum + (client.totalDepense || 0), 0)) },
        { label: "Panier moyen", value: formatCurrencyForPDF(filteredClients.reduce((sum, client) => sum + (client.totalDepense || 0), 0) / filteredClients.filter(c => c.totalDepense > 0).length) }
      ];
      
      doc.autoTable({
        startY: 55,
        head: [['Indicateur', 'Valeur']],
        body: stats.map(item => [item.label, item.value]),
        theme: 'grid',
        headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: 'bold' },
        styles: { fontSize: 10 },
        margin: { left: margin, right: margin },
        tableWidth: 'auto',
        alternateRowStyles: { fillColor: [245, 247, 250] }
      });
      
      // Add clients table
      const tableColumn = [
        "Nom", 
        "Email", 
        "Téléphone", 
        "Localisation", 
        "Statut", 
        "Fidélité", 
        "Réservations", 
        "Total Dépensé", 
        "Inscription"
      ];
      
      const tableRows = filteredClients.map(client => [
        `${client.prenom} ${client.nom}`,
        client.email,
        client.telephone,
        client.adresse,
        client.statut,
        client.niveauFidelite,
        getClientReservations(client.id).length,
        formatCurrencyForPDF(client.totalDepense),
        new Date(client.dateInscription).toLocaleDateString('fr-FR')
      ]);
      
      doc.autoTable({
        startY: doc.lastAutoTable.finalY + 20,
        head: [tableColumn],
        body: tableRows,
        theme: 'grid',
        headStyles: { fillColor: secondaryColor, textColor: [255, 255, 255], fontStyle: 'bold' },
        styles: { fontSize: 9, cellPadding: 3 },
        columnStyles: {
          0: { cellWidth: 40 },
          1: { cellWidth: 45 },
          2: { cellWidth: 30 },
          3: { cellWidth: 40 },
          4: { cellWidth: 20 },
          5: { cellWidth: 20 },
          6: { cellWidth: 25 },
          7: { cellWidth: 30 },
          8: { cellWidth: 25 }
        },
        margin: { left: margin, right: margin },
        tableWidth: 'auto',
        alternateRowStyles: { fillColor: [245, 247, 250] },
        didDrawPage: function (data) {
          // Add header
          doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
          doc.rect(0, 0, pageWidth, 25, 'F');
          
          // Add agency logo and name in header
          if (settings.agencyLogo) {
            try {
              // Add the agency logo from base64 string
              doc.addImage(settings.agencyLogo, 'PNG', margin, 5, 20, 15, undefined, 'FAST');
              
              // Add agency name
              doc.setTextColor(255, 255, 255);
              doc.setFontSize(12);
              doc.setFont('helvetica', 'bold');
              doc.text(settings.agencyName || 'Agence de Voyage', margin + 25, 15);
            } catch (logoError) {
              console.error('Error adding logo to PDF:', logoError);
              // Fallback to text only
              doc.setTextColor(255, 255, 255);
              doc.setFontSize(12);
              doc.setFont('helvetica', 'bold');
              doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
            }
          } else {
            // No logo, just add agency name
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
          }
          
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(16);
          doc.setFont('helvetica', 'bold');
          doc.text("Liste des Clients", pageWidth - margin, 17, { align: 'right' });
          
          // Add footer
          const pageNumber = doc.internal.getNumberOfPages();
          doc.setFontSize(10);
          doc.setTextColor(100, 100, 100);
          doc.text(`Page ${pageNumber}`, margin, pageHeight - 10);
          doc.text(`${settings.appName} - Système de Gestion pour Agence de Voyage`, pageWidth / 2, pageHeight - 10, { align: 'center' });
          doc.text(`Généré le: ${new Date().toLocaleDateString('fr-FR')}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
        }
      });
      
      // Save the PDF
      doc.save('liste-clients.pdf');
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Export PDF réussi',
        severity: 'success'
      });
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de l\'export PDF',
        severity: 'error'
      });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 700,
            background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)',
            backgroundClip: 'text',
            textFillColor: 'transparent',
            mb: 1
          }}
        >
          Gestion des Clients
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Gérez votre base de données clients et suivez leurs activités
        </Typography>
      </Box>

      {/* Loading Indicator */}
      {isLoading ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '50vh' 
        }}>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h6">
            Chargement des données clients...
          </Typography>
        </Box>
      ) : (
        <>
          {/* Statistics Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {statsCards.map((stat, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card 
                  sx={{ 
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(20px)',
                    borderRadius: 3,
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                          {stat.value}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {stat.title}
                        </Typography>
                        <Chip 
                          label={stat.trend} 
                          size="small" 
                          color={stat.color}
                          sx={{ mt: 1 }}
                        />
                      </Box>
                      <Avatar 
                        sx={{ 
                          bgcolor: `${stat.color}.main`,
                          width: 56,
                          height: 56
                        }}
                      >
                        <stat.icon />
                      </Avatar>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Search and Filters */}
          <Card 
            sx={{ 
              mb: 3,
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(20px)',
              borderRadius: 3,
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}
          >
            <CardContent>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    placeholder="Rechercher un client..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Statut</InputLabel>
                    <Select
                      value={filterStatus}
                      label="Statut"
                      onChange={(e) => setFilterStatus(e.target.value)}
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value="all">Tous</MenuItem>
                      <MenuItem value="Actif">Actifs</MenuItem>
                      <MenuItem value="Inactif">Inactifs</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={<ExportIcon />}
                      onClick={exportClientsToPDF}
                      sx={{ borderRadius: 2, flex: 1 }}
                    >
                      Exporter
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => handleOpenDialog()}
                      sx={{ 
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)',
                        flex: 1
                      }}
                    >
                      Ajouter
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Clients Table */}
          <Card 
            sx={{ 
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(20px)',
              borderRadius: 3,
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}
          >
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Contact</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Localisation</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Statut</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Fidélité</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Réservations</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Total Dépensé</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredClients
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((client) => (
                      <TableRow key={client.id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar 
                              sx={{ bgcolor: 'primary.main', mr: 2 }}
                            >
                              {getInitials(client.nom, client.prenom)}
                            </Avatar>
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {client.prenom} {client.nom}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Inscrit le {client.dateInscription}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <EmailIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                              <Typography variant="body2">{client.email}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PhoneIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                              <Typography variant="body2">{client.telephone}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocationIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                            <Typography variant="body2">
                              {client.ville && client.pays 
                                ? `${client.ville}, ${client.pays}`
                                : client.ville || client.pays || client.adresse || "Non spécifiée"}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={client.statut}
                            color={client.statut === 'Actif' ? 'success' : 'default'}
                            size="small"
                            sx={{ minWidth: 80 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={client.niveauFidelite}
                            color={getFidelityColor(client.niveauFidelite)}
                            size="small"
                            sx={{ minWidth: 80 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Badge
                              badgeContent={client.nombreReservations}
                              color="info"
                              sx={{ 
                                '& .MuiBadge-badge': { 
                                  right: -5, 
                                  top: -5 
                                } 
                              }}
                            >
                              <FlightIcon sx={{ fontSize: 20, mr: 0.5 }} />
                            </Badge>
                            <Typography variant="body2" sx={{ ml: 1 }}>
                              {client.nombreReservations === 1 ? '1 voyage' : `${client.nombreReservations} voyages`}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {formatCurrency(client.totalDepense)} DZD
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <IconButton 
                            size="small"
                            onClick={(e) => handleMenuClick(e, client)}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            {filteredClients.length > rowsPerPage ? (
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                component="div"
                count={filteredClients.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
              />
            ) : (
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Typography variant="body2" color="text.secondary">
                  {filteredClients.length === 0 
                    ? 'Aucun client' 
                    : filteredClients.length === 1 
                      ? '1 client affiché' 
                      : `${filteredClients.length} clients affichés`}
                </Typography>
              </Box>
            )}
          </Card>
        </>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }
        }}
      >
        <MenuListItem onClick={() => { handleOpenDialog(selectedClient); handleMenuClose(); }}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Modifier</ListItemText>
        </MenuListItem>
        <MenuListItem onClick={handleViewDetails}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Voir détails</ListItemText>
        </MenuListItem>
        <MenuListItem onClick={() => { handleDeleteClient(selectedClient?.id); handleMenuClose(); }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>Supprimer</ListItemText>
        </MenuListItem>
      </Menu>

      {/* Add/Edit Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {editingClient ? 'Modifier le client' : 'Ajouter un nouveau client'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nom"
                value={formData.nom}
                onChange={(e) => setFormData({...formData, nom: e.target.value})}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Prénom"
                value={formData.prenom}
                onChange={(e) => setFormData({...formData, prenom: e.target.value})}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Téléphone"
                value={formData.telephone}
                onChange={(e) => setFormData({...formData, telephone: e.target.value})}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Adresse"
                value={formData.adresse}
                onChange={(e) => setFormData({...formData, adresse: e.target.value})}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Ville"
                value={formData.ville}
                onChange={(e) => setFormData({...formData, ville: e.target.value})}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Pays"
                value={formData.pays}
                onChange={(e) => setFormData({...formData, pays: e.target.value})}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Statut</InputLabel>
                <Select
                  value={formData.statut}
                  label="Statut"
                  onChange={(e) => setFormData({...formData, statut: e.target.value})}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="Actif">Actif</MenuItem>
                  <MenuItem value="Inactif">Inactif</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={handleCloseDialog} sx={{ borderRadius: 2 }}>
            Annuler
          </Button>
          <Button 
            onClick={handleSaveClient}
            variant="contained"
            sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)'
            }}
          >
            {editingClient ? 'Modifier' : 'Ajouter'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Client Details Dialog with Interconnected Data */}
      <Dialog 
        open={openDetailsDialog} 
        onClose={() => setOpenDetailsDialog(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            minHeight: '600px'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar 
              sx={{ 
                bgcolor: 'primary.main',
                width: 50,
                height: 50
              }}
            >
              {selectedClient && getInitials(selectedClient.nom, selectedClient.prenom)}
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {selectedClient && `${selectedClient.prenom} ${selectedClient.nom}`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedClient && `Client depuis le ${formatDate(selectedClient.dateInscription)}`}
              </Typography>
            </Box>
            <Box sx={{ ml: 'auto' }}>
              <Chip
                label={selectedClient?.niveauFidelite}
                color={getFidelityColor(selectedClient?.niveauFidelite)}
                sx={{ borderRadius: 2 }}
              />
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Tabs 
            value={detailsTab} 
            onChange={(e, newValue) => setDetailsTab(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}
          >
            <Tab label="Informations Générales" />
            <Tab label="Réservations" />
            <Tab label="Transactions" />
            <Tab label="Statistiques" />
          </Tabs>

          {/* Tab Panel 0: General Information */}
          {detailsTab === 0 && (
            <Box sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card sx={{ p: 2, background: 'rgba(30, 64, 175, 0.05)', borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Contact</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <EmailIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                      <Typography>{selectedClient?.email}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <PhoneIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                      <Typography>{selectedClient?.telephone}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                      <Typography>{selectedClient?.adresse}</Typography>
                    </Box>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card sx={{ p: 2, background: 'rgba(124, 58, 237, 0.05)', borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Préférences</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {selectedClient?.preferencesVoyage?.map((pref, index) => (
                        <Chip 
                          key={index}
                          label={pref}
                          size="small"
                          color="secondary"
                          sx={{ borderRadius: 2 }}
                        />
                      ))}
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Tab Panel 1: Reservations */}
          {detailsTab === 1 && (
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Historique des Réservations ({getClientReservations(selectedClient?.id).length})
              </Typography>
              {getClientReservations(selectedClient?.id).length > 0 ? (
                <List>
                  {getClientReservations(selectedClient?.id).map((reservation, index) => (
                    <React.Fragment key={reservation.id}>
                      <ListItem 
                        sx={{ 
                          background: 'rgba(255, 255, 255, 0.8)',
                          borderRadius: 2,
                          mb: 1,
                          border: '1px solid rgba(0,0,0,0.1)'
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'primary.main' }}>
                            <FlightIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={reservation.destination}
                          secondary={
                            <Box>
                              <Typography variant="caption" display="block">
                                Du {formatDate(reservation.dateDepart)} au {formatDate(reservation.dateRetour)}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                                <Chip 
                                  label={reservation.statut}
                                  size="small"
                                  color={reservation.statut === 'Confirmé' ? 'success' : 'warning'}
                                  sx={{ borderRadius: 2 }}
                                />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  {formatCurrency(reservation.prix)}
                                </Typography>
                              </Box>
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < getClientReservations(selectedClient?.id).length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary">Aucune réservation trouvée</Typography>
              )}
            </Box>
          )}

          {/* Tab Panel 2: Transactions */}
          {detailsTab === 2 && (
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Historique des Paiements ({getClientTransactions(selectedClient?.id).length})
              </Typography>
              {getClientTransactions(selectedClient?.id).length > 0 ? (
                <List>
                  {getClientTransactions(selectedClient?.id).map((transaction, index) => (
                    <React.Fragment key={transaction.id}>
                      <ListItem 
                        sx={{ 
                          background: 'rgba(255, 255, 255, 0.8)',
                          borderRadius: 2,
                          mb: 1,
                          border: '1px solid rgba(0,0,0,0.1)'
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'success.main' }}>
                            <PaymentIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={transaction.description}
                          secondary={
                            <Box>
                              <Typography variant="caption" display="block">
                                {formatDate(transaction.date)}
                              </Typography>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'success.main' }}>
                                {formatCurrency(transaction.montant)}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < getClientTransactions(selectedClient?.id).length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary">Aucune transaction trouvée</Typography>
              )}
            </Box>
          )}

          {/* Tab Panel 3: Statistics */}
          {detailsTab === 3 && (
            <Box sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Card sx={{ p: 2, textAlign: 'center', background: 'rgba(30, 64, 175, 0.05)' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      {selectedClient?.nombreReservations || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Réservations Total
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card sx={{ p: 2, textAlign: 'center', background: 'rgba(34, 197, 94, 0.05)' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                      {formatCurrency(selectedClient?.totalDepense || 0)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Dépensé
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card sx={{ p: 2, textAlign: 'center', background: 'rgba(124, 58, 237, 0.05)' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'secondary.main' }}>
                      {selectedClient?.totalDepense && selectedClient?.nombreReservations ? 
                        formatCurrency(selectedClient.totalDepense / selectedClient.nombreReservations) : 
                        formatCurrency(0)
                      }
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Panier Moyen
                    </Typography>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={() => setOpenDetailsDialog(false)} sx={{ borderRadius: 2 }}>
            Fermer
          </Button>
          <Button 
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => {
              setOpenDetailsDialog(false);
              handleOpenDialog(selectedClient);
            }}
            sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)'
            }}
          >
            Modifier Client
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({...snackbar, open: false})}
      >
        <Alert 
          onClose={() => setSnackbar({...snackbar, open: false})} 
          severity={snackbar.severity}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Clients; 